/**
 * <tecton-word-reveal> — per-word blur-up entrance.
 *
 * Liquid port of WordReveal.tsx. Splits its text into words and staggers them
 * in at 60ms intervals, each fading from 0.1 opacity with an 8px rise and a
 * 6px blur. Words listed in `data-accent` render in steel blue.
 *
 * Splitting happens at runtime rather than in Liquid so the markup stays a
 * single readable string in the theme editor, and so a merchant editing the
 * heading never has to think about span boundaries.
 *
 * Usage:
 *   <tecton-word-reveal data-accent="ketones.|easier.">Your body already…</tecton-word-reveal>
 */

const MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const STAGGER_MS = 60;

class TectonWordReveal extends HTMLElement {
  connectedCallback() {
    // Guard against the theme editor re-running connectedCallback on an
    // element we have already split, which would nest spans on every edit.
    if (this.dataset.split === '') return;

    const text = this.textContent.trim();
    if (!text) return;

    const accents = new Set((this.dataset.accent || '').split('|').filter(Boolean));

    const frag = document.createDocumentFragment();
    text.split(/\s+/).forEach((word, i) => {
      const span = document.createElement('span');
      span.className = 'tecton-word';
      if (accents.has(word)) span.classList.add('tecton-word--accent');
      span.style.transitionDelay = `${i * STAGGER_MS}ms`;
      span.textContent = word;
      frag.append(span, document.createTextNode(' '));
    });

    this.textContent = '';
    this.append(frag);
    this.dataset.split = '';

    if (window.matchMedia(MOTION_QUERY).matches || window.Shopify?.designMode) {
      this.dataset.revealed = '';
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.dataset.revealed = '';
        observer.disconnect();
      },
      { threshold: 0.3 }
    );
    observer.observe(this);
    this.observer = observer;
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('tecton-word-reveal')) {
  customElements.define('tecton-word-reveal', TectonWordReveal);
}
